import {
    HttpClient,
    HttpDownloadProgressEvent,
    HttpEventType,
    HttpParams,
    HttpRequest,
    HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface ChatImage {
    id: string;
    url: string;
    operation?: string;
    prompt?: string;
    width?: number;
    height?: number;
    parent_image_ids?: string[];
}

export interface ChatMessageDto {
    role: 'user' | 'assistant';
    content: string;
    images: { id: string; url: string }[];
    videos?: { id?: string; url: string }[];
    created_at: string;
}

export interface ChatSessionDto {
    id: string;
    title: string;
    model: string;
    created_at: string;
    updated_at: string;
}

export interface ChatRequest {
    session_id?: string;
    message?: string;
    attached_image_ids?: string[];
    customer_id?: string;
    user_id?: string;
    model?: string;
    controls?: { [key: string]: any };
    confirm?: boolean;
    pending?: any;
    /*
     * Answer to an action:'choose_upscaler' turn — 'auto', or one of the option
     * keys the orchestrator offered ('standard', 'bria'). Sent with the original
     * turn replayed unchanged, alongside the turn_id it came back with.
     */
    upscale_model?: string;
    turn_id?: string;
}

/** One upscaler the orchestrator offers on an action:'choose_upscaler' turn. */
export interface UpscalerOption {
    key: string;
    label: string;
    subtitle?: string;
    cost_hint?: string;
    available?: boolean;
}

/*
 * One event from the /converse/stream NDJSON feed.
 *  - {event:'step',   label}    -> a tool/step is running
 *  - {event:'result', ...}      -> final turn payload (action, ai_message, new_images…)
 *  - {event:'error',  detail}   -> upstream/proxy failure
 */
export interface StreamEvent {
    event: 'step' | 'result' | 'error' | string;
    label?: string;
    detail?: string;
    action?: string;
    session_id?: string;
    ai_message?: string;
    new_images?: ChatImage[];
    model_used?: string;
    model_label?: string;
    aspect_used?: string;
    job?: any;
    pending?: any;
    /* action:'choose_upscaler' — which upscaler to run, asked before anything is spent. */
    target?: string;
    options?: UpscalerOption[];
    source_size?: number[];
    image_id?: string;
    turn_id?: string;
    [key: string]: any;
}

export interface ChatResponse {
    session_id: string;
    ai_message: string;
    new_images: ChatImage[];
    iterations: number;
    elapsed_seconds: number;
    account_id: string;
}

export interface UploadResponse {
    id: string;
    url: string;
    width: number;
    height: number;
    session_id?: string;
}

/* ---- Studio API types ---- */

export interface GenerateRequest {
    prompt: string;
    mode?: string;                 // model key from generation-models
    session_id?: string | null;
    aspect_ratio?: string;
    output_size?: string;
    num_images?: number;
    seed?: number | null;
    style_preset?: string;
    negative_prompt?: string;
    customer_id?: string;
}

export interface GenerateResponse {
    session_id: string;
    ai_message: string;
    mode?: string;
    new_images: ChatImage[];
}

export type EditTool =
    | 'remove_bg' | 'fill' | 'erase' | 'replace_bg' | 'face_restore'
    | 'restore' | 'colorize' | 'denoise' | 'crop';

export interface EditRequest {
    tool: EditTool;
    session_id?: string | null;
    image_id?: string;
    image_ids?: string[];
    prompt?: string;
    mask?: string;
    outpaint?: any;
    scale?: number;
    fidelity?: number;
    upscale?: number;
    noise?: number;
    x?: number;
    y?: number;
    w?: number;
    h?: number;
    user_id?: string;
    customer_id?: string;
}

export interface EditResponse {
    session_id: string;
    ai_message: string;
    operation: string;
    new_images: ChatImage[];
}

export interface FaceRestoreRequest {
    session_id?: string | null;
    user_id?: string;
    image_id: string;
    scale?: number;
    customer_id?: string;
}

export interface RestoreImageRequest {
    session_id?: string | null;
    user_id?: string;
    image_id: string;
    customer_id?: string;
}

export interface FillImageRequest {
    session_id?: string | null;
    user_id?: string;
    image_id: string;
    mask: string;
    prompt?: string;
    customer_id?: string;
}

export interface EraseImageRequest {
    session_id?: string | null;
    user_id?: string;
    image_id: string;
    mask: string;
    customer_id?: string;
}

export interface VideoTextRequest {
    prompt: string;
    model: string;
    duration?: number;
    resolution?: string;
    aspect_ratio?: string;
    seed?: number | null;
    camera_fixed?: boolean;
    audio_url?: string;
    session_id?: string | null;
    customer_id?: string;
}

export interface VideoImageRequest {
    image_id: string;
    prompt?: string;
    model: string;
    duration?: number;
    resolution?: string;
    aspect_ratio?: string;
    audio_url?: string;
    session_id?: string | null;
    customer_id?: string;
}

export interface TalkingHeadRequest {
    session_id?: string | null;
    image_id: string;
    audio_url: string;
    enhance?: boolean;
    customer_id?: string;
}

export interface VideoJob {
    job_id: string;
    status: 'queued' | 'processing' | 'succeeded' | 'failed';
    output?: { url: string };
    error?: string;
    model?: string;
    operation?: string;
    session_id?: string;
}

export interface JobListResponse {
    jobs: (VideoJob & {
        created_at?: string;
        kind?: string;
    })[];
}

export interface ConverseRequest {
    message: string;
    session_id?: string | null;
    model?: string;
    controls?: { [key: string]: any };
    confirm?: boolean;
    pending?: any;
    customer_id?: string;
}

export interface ConverseResponse {
    action: 'chat' | 'create' | 'refine' | 'confirm' | 'open_tool' | string;
    session_id?: string;
    ai_message: string;
    new_images?: ChatImage[];
    job?: VideoJob;
    pending?: any;
    model_used?: string;
}

/** One tool line the customer's plan sells, from their credit-pack snapshot. */
export interface PermissionTool {
    service_code: string;
    service_label?: string;
    group_key?: string;
    sell_credits: number;
    max_uses_per_month: number | null;
}

/** GET /user-permission — the seat's grants joined to an unexpired plan. */
export interface UserPermissionResponse {
    status: boolean;
    data: {
        customer_id: string;
        collection: string;
        comp_collection?: string;
        search_collection?: string;
        user_size?: string;
        customer_plan_id?: number;
        validity?: string;
    }[];
    tools: PermissionTool[];
}

export interface CustomerDto {
    customer_id: string;
    name?: string;
    [key: string]: any;
}

export interface CustomersResponse {
    customers: CustomerDto[];
}

export interface ControlOption {
    value: any;
    label: string;
    group?: string;
}

export interface ModelControl {
    key: string;
    type: 'select' | 'number' | 'textarea' | 'seed' | string;
    label: string;
    default?: any;
    options?: ControlOption[];
    min?: number;
    max?: number;
    placeholder?: string;
    help?: string;
}

export interface ModelInfo {
    key: string;
    model: string;
    kind: 'image' | 'video';
    label?: string;
    subtitle?: string;
    max_images?: number;
    supports_seed?: boolean;
    supports_negative_prompt?: boolean;
    sizes?: { key: string; label: string }[];
    available?: boolean;
    controls?: ModelControl[];
    notes?: string;
}

export interface GenerationModelsResponse {
    models: ModelInfo[];
    default: string;
    aspect_ratios: string[];
    style_presets: string[];
}

export interface CreditToolUsage {
    tool?: string;
    name?: string;
    llm?: string;
    count?: number;
    deducted?: number;
    credits?: number;
    spent?: number;
    label?: string;
    used?: number;
}

export interface CreditsResponse {
    customer_id: string | null;
    customer_name?: string;
    has_plan?: boolean;
    // The studio may return the credit fields nested under `credits` or flat on
    // the root object; both shapes are normalised in the component.
    plan?: string;
    total?: number;
    deducted?: number;
    remaining?: number;
    reserved?: number;
    remaining_inr?: number;
    credit_value_inr?: number;
    tools?: CreditToolUsage[];
    credits: {
        plan: string;
        total: number;
        deducted: number;
        remaining: number;
        reserved: number;
        remaining_inr: number;
        credit_value_inr: number;
        tools?: CreditToolUsage[];
    } | null;
}

export interface SessionUsageResponse {
    session_id: string;
    total_usd: number;
    total_inr: number;
    exchange_rate: number;
    ops: number;
    by_kind: {
        model_kind: string;
        usd: number;
        inr: number;
        count: number;
    }[];
}

export interface PricingModel {
    model: string;
    unit: string;                          // 'tokens' | 'image' | 'video'
    usd_per_1m_input_tokens?: number;
    usd_per_1m_output_tokens?: number;
    usd_per_image?: number;
    usd_per_video?: number;
    usd_per_second?: number;
    quality_multipliers?: { [k: string]: number };
}

export interface PricingTableResponse {
    categories: { [category: string]: PricingModel[] };
    aliases: { [alias: string]: string };
    fallback_usd_inr: number;
    raw_usd_inr: number;
    current_usd_inr: number;
    fx_margin_pct: number;
    fx_round: string;
}

export interface PricingRequest {
    model: string;
    count?: number;
    input_tokens?: number;
    output_tokens?: number;
    exchange_rate?: number;
}

export interface PricingResponse {
    model: string;
    category: string;
    usd_cost: number;
    inr_cost: number;
    currency: string;
    exchange_rate: number;
}

export interface UsageSummaryResponse {
    account_id: string;
    summary: {
        from: string;
        to: string;
        calls: number;
        prompt_tokens: number;
        completion_tokens: number;
        total_tokens: number;
        cost_usd: number;
    };
    by_day: { key: string; calls: number; cost_usd: number; total_tokens: number }[];
}

@Injectable({ providedIn: 'root' })
export class ImageChatService {
    /**
     * Which backend serves the AI chat / Studio endpoints.
     *
     * IndiaPicture customers are served by Oxzin\IndiaPicture\Classes\AiChat under
     * api/indiapicture/auth/, which charges the non-exclusive credit pack tables;
     * everyone else by the exclusive Helper under api/indiapictureexclusive/auth/.
     * The two expose the same route names, so only the prefix differs.
     *
     * A getter rather than a field: the service is a root singleton constructed
     * once — possibly before sign-in — while user_type is only known from the
     * moment the login flow writes it, and changes if the customer signs out and
     * back in as the other type.
     */
    private get base(): string {
        return (this.isIndiaPictureUser
            ? environment.backendUrl
            : environment.exclusiveBackendUrl) + 'auth/';
    }

    /**
     * localStorage.currentUser.user_type, as AuthenticationService.login() writes
     * it ('INDIAPICTURE' | 'EXCLUSIVE' | 'BUDGET').
     *
     * Read straight from storage rather than through AuthenticationService to keep
     * this service free of that dependency — it is only ever a string compare. A
     * signed-out visitor, or storage holding something unparseable, falls back to
     * the exclusive backend, which is where these calls went before the split.
     */
    private get isIndiaPictureUser(): boolean {
        try {
            const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
            return Boolean(currentUser?.isExclusive === false || currentUser?.user_type === 'INDIAPICTURE');
        } catch {
            return false;
        }
    }

    constructor(private http: HttpClient) {}

    sendMessage(req: ChatRequest, skipLoader = true): Observable<ChatResponse> {
        return this.http.post<ChatResponse>(this.base + 'sendMessage', req, {
            params: { skipLoader: String(skipLoader) }
        });
    }

    /*
     * Streaming send — proxies the upstream /converse/stream NDJSON feed and
     * emits each parsed event ({event:'step'|'result'|'error', …}) as it arrives,
     * so the UI can show every tool step live and then render the final images.
     *
     * Uses HttpClient with reportProgress so we receive the growing response
     * body incrementally; a brace-depth scanner pulls out complete JSON objects
     * (robust to either newline-delimited or back-to-back `}{` framing).
     */
    sendMessageStream(req: ChatRequest, skipLoader = true): Observable<StreamEvent> {
        return new Observable<StreamEvent>(observer => {
            const httpReq = new HttpRequest('POST', this.base + 'sendMessage', req, {
                params: new HttpParams().set('skipLoader', String(skipLoader)),
                reportProgress: true,
                responseType: 'text'
            });

            let buffer = '';       // unparsed tail (an incomplete object)
            let consumed = 0;      // chars of the cumulative partialText already ingested

            const ingest = (fullText: string) => {
                if (fullText.length <= consumed) { return; }
                buffer += fullText.slice(consumed);
                consumed = fullText.length;
                const { objects, rest } = this.extractJsonObjects(buffer);
                buffer = rest;
                for (const obj of objects) {
                    observer.next(obj as StreamEvent);
                }
            };

            const sub = this.http.request(httpReq).subscribe({
                next: (ev) => {
                    if (ev.type === HttpEventType.DownloadProgress) {
                        ingest((ev as HttpDownloadProgressEvent).partialText || '');
                    } else if (ev.type === HttpEventType.Response) {
                        ingest((ev as HttpResponse<string>).body || '');
                        observer.complete();
                    }
                },
                error: (e) => observer.error(e)
            });

            return () => sub.unsubscribe();
        });
    }

    /*
     * Extract every complete top-level JSON object from `buffer`, returning the
     * parsed objects plus any trailing partial fragment still to be completed.
     * Brace-aware and string/escape-aware, so it works whether the upstream
     * separates events with newlines or concatenates them directly.
     */
    private extractJsonObjects(buffer: string): { objects: any[]; rest: string } {
        const objects: any[] = [];
        let depth = 0;
        let start = -1;
        let inString = false;
        let escaped = false;
        let lastEnd = 0;

        for (let i = 0; i < buffer.length; i++) {
            const ch = buffer[i];
            if (inString) {
                if (escaped) { escaped = false; }
                else if (ch === '\\') { escaped = true; }
                else if (ch === '"') { inString = false; }
                continue;
            }
            if (ch === '"') { inString = true; continue; }
            if (ch === '{') {
                if (depth === 0) { start = i; }
                depth++;
            } else if (ch === '}') {
                depth--;
                if (depth === 0 && start >= 0) {
                    const chunk = buffer.slice(start, i + 1);
                    try {
                        objects.push(JSON.parse(chunk));
                    } catch {
                        // Not yet a valid object — leave it for the next chunk.
                    }
                    lastEnd = i + 1;
                    start = -1;
                }
            }
        }

        // Anything after the last completed object is an incomplete fragment.
        const rest = depth > 0 && start >= 0 ? buffer.slice(start) : buffer.slice(lastEnd);
        return { objects, rest };
    }

    listSessions(limit = 50, customerId: any, skipLoader = true): Observable<{ sessions: ChatSessionDto[] }> {
        return this.http.get<{ sessions: ChatSessionDto[] }>(this.base + 'listSessions', {
            params: { limit: String(limit), customer_id: customerId, skipLoader: String(skipLoader) }
        });
    }

    getHistory(sessionId: string, skipLoader = true): Observable<{ session_id: string; messages: ChatMessageDto[] }> {
        return this.http.get<{ session_id: string; messages: ChatMessageDto[] }>(this.base + 'getHistory', {
            params: { session_id: sessionId, skipLoader: String(skipLoader) }
        });
    }

    uploadImage(file: File, sessionId: string | null = null, currentUser: any, skipLoader = true): Observable<UploadResponse> {
        const formData = new FormData();
        formData.append('file', file);
        const params: { [key: string]: string } = { skipLoader: String(skipLoader) };
        // Same identifier the rest of the studio calls send, so a session opened
        // by this upload is attributed to the customer and lists in the sidebar.
        if (currentUser?.customers_id) {
            params['customer_id'] = String(currentUser.customers_id);
        }
        if (sessionId) {
            params['session_id'] = sessionId;
        }
        return this.http.post<UploadResponse>(this.base + 'uploadImage', formData, { params });
    }

    /* ---- Studio API ---- */

    generateImage(req: GenerateRequest, skipLoader = true): Observable<GenerateResponse> {
        return this.http.post<GenerateResponse>(this.base + 'generateImage', req, {
            params: { skipLoader: String(skipLoader) }
        });
    }

    editImage(req: EditRequest, skipLoader = true): Observable<EditResponse> {
        return this.http.post<EditResponse>(this.base + 'editImage', req, {
            params: { skipLoader: String(skipLoader) }
        });
    }

    faceRestore(req: FaceRestoreRequest, skipLoader = true): Observable<EditResponse> {
        return this.http.post<EditResponse>(this.base + 'faceRestore', req, {
            params: { skipLoader: String(skipLoader) }
        });
    }

    restoreImage(req: RestoreImageRequest, skipLoader = true): Observable<EditResponse> {
        return this.http.post<EditResponse>(this.base + 'restoreImage', req, {
            params: { skipLoader: String(skipLoader) }
        });
    }

    fillImage(req: FillImageRequest, skipLoader = true): Observable<EditResponse> {
        return this.http.post<EditResponse>(this.base + 'fillImage', req, {
            params: { skipLoader: String(skipLoader) }
        });
    }

    eraseImage(req: EraseImageRequest, skipLoader = true): Observable<EditResponse> {
        return this.http.post<EditResponse>(this.base + 'eraseImage', req, {
            params: { skipLoader: String(skipLoader) }
        });
    }

    generateVideoText(req: VideoTextRequest, skipLoader = true): Observable<VideoJob> {
        return this.http.post<VideoJob>(this.base + 'generateVideoText', req, {
            params: { skipLoader: String(skipLoader) }
        });
    }

    generateVideoImage(req: VideoImageRequest, skipLoader = true): Observable<VideoJob> {
        return this.http.post<VideoJob>(this.base + 'generateVideoImage', req, {
            params: { skipLoader: String(skipLoader) }
        });
    }

    talkingHead(req: TalkingHeadRequest, skipLoader = true): Observable<VideoJob> {
        return this.http.post<VideoJob>(this.base + 'talkingHead', req, {
            params: { skipLoader: String(skipLoader) }
        });
    }

    getVideoJob(jobId: string, skipLoader = true): Observable<VideoJob> {
        return this.http.get<VideoJob>(this.base + 'videoJob', {
            params: { job_id: jobId, skipLoader: String(skipLoader) }
        });
    }

    uploadAudio(file: File, sessionId: string | null = null, skipLoader = true): Observable<{ url: string }> {
        const formData = new FormData();
        formData.append('file', file);
        const params: { [key: string]: string } = { skipLoader: String(skipLoader) };
        if (sessionId) {
            params['session_id'] = sessionId;
        }
        return this.http.post<{ url: string }>(this.base + 'uploadAudio', formData, { params });
    }

    getGenerationModels(skipLoader = true): Observable<GenerationModelsResponse> {
        return this.http.get<GenerationModelsResponse>(this.base + 'generationModels', {
            params: { skipLoader: String(skipLoader) }
        });
    }

    getEditTools(skipLoader = true): Observable<{ tools: ModelInfo[] }> {
        return this.http.get<{ tools: ModelInfo[] }>(this.base + 'editTools', {
            params: { skipLoader: String(skipLoader) }
        });
    }

    getVideoModels(skipLoader = true): Observable<{ models: ModelInfo[] }> {
        return this.http.get<{ models: ModelInfo[] }>(this.base + 'videoModels', {
            params: { skipLoader: String(skipLoader) }
        });
    }

    getStudioUserId(skipLoader = true): Observable<{ user_id: string | null }> {
        return this.http.get<{ user_id: string | null }>(this.base + 'studioUserKey', {
            params: { skipLoader: String(skipLoader) }
        });
    }

    getCredits(sessionId: string, skipLoader = true): Observable<CreditsResponse> {
        return this.http.get<CreditsResponse>(this.base + 'credits', {
            params: { session_id: sessionId, skipLoader: String(skipLoader) }
        });
    }

    getSessionUsage(sessionId: string, userId: string, skipLoader = true): Observable<SessionUsageResponse> {
        return this.http.get<SessionUsageResponse>(this.base + 'sessionUsage', {
            params: { session_id: sessionId, user_id: userId, skipLoader: String(skipLoader) }
        });
    }

    getPricingTable(skipLoader = true): Observable<PricingTableResponse> {
        return this.http.get<PricingTableResponse>(this.base + 'pricingTable', {
            params: { skipLoader: String(skipLoader) }
        });
    }

    calculatePricing(req: PricingRequest, skipLoader = true): Observable<PricingResponse> {
        return this.http.post<PricingResponse>(this.base + 'pricing', req, {
            params: { skipLoader: String(skipLoader) }
        });
    }

    getUsageSummary(start?: string, end?: string, skipLoader = true): Observable<UsageSummaryResponse> {
        const params: { [key: string]: string } = { skipLoader: String(skipLoader) };
        if (start) { params['start'] = start; }
        if (end) { params['end'] = end; }
        return this.http.get<UsageSummaryResponse>(this.base + 'usageSummary', { params });
    }

    /* ---- Orchestrator turn (chat / create / refine) ---- */
    converse(req: ConverseRequest, skipLoader = true): Observable<ConverseResponse> {
        return this.http.post<ConverseResponse>(this.base + 'converse', req, {
            params: { skipLoader: String(skipLoader) }
        });
    }

    /**
     * Cancel the turn that is still running upstream.
     *
     * `turnId` is the orchestrator's id for the turn, which the stream repeats on
     * every event — see StreamEvent.turn_id.
     *
     * skipLoader stays true: the composer has already swapped the stop button back
     * to send by the time this is called, and a global spinner over a cancel would
     * make stopping look like more waiting.
     */
    stopConverse(turnId: string, skipLoader = true): Observable<any> {
        return this.http.post<any>(this.base + 'converseStop', { turn_id: turnId }, {
            params: { skipLoader: String(skipLoader) }
        });
    }

    /* ---- Recent async jobs (video / talking-head / async edits) ---- */
    listJobs(limit = 20, skipLoader = true): Observable<JobListResponse> {
        return this.http.get<JobListResponse>(this.base + 'listJobs', {
            params: { limit: String(limit), skipLoader: String(skipLoader) }
        });
    }

    /* ---- Customer directory (for scoping sessions to a customer_id) ---- */
    getCustomers(skipLoader = true): Observable<CustomersResponse> {
        return this.http.get<CustomersResponse>(this.base + 'customers', {
            params: { skipLoader: String(skipLoader) }
        });
    }

    /*
     * What this customer may use: the seat's collection grant plus the tool
     * lines their plan sells (service_code, sell_credits, max_uses_per_month).
     *
     * Sits one level up from `base` — /user-permission is outside the auth/
     * prefix the studio endpoints share.
     */
    getUserPermission(customerId: string, skipLoader = true): Observable<UserPermissionResponse> {
        const root = this.isIndiaPictureUser
            ? environment.backendUrl
            : environment.exclusiveBackendUrl;

        return this.http.get<UserPermissionResponse>(
            root + 'user-permission',
            { params: { customer_id: customerId, skipLoader: String(skipLoader) } }
        );
    }

    /**
     * The customer's plan-side credit balance.
     *
     * The fallback for the credits badge: getCredits() is keyed by chat session
     * and has nothing to report until one is open, while this is true from the
     * moment a pack is activated.
     *
     * Same root selection and same level as getUserPermission() — it is the twin
     * of that endpoint on both backends, and takes no id because the backend
     * reads the customer from the token.
     */
    getPlanCredits(skipLoader = true): Observable<any> {
        const root = this.isIndiaPictureUser
            ? environment.backendUrl
            : environment.exclusiveBackendUrl;

        return this.http.get<any>(root + 'plan-credits', {
            params: { skipLoader: String(skipLoader) }
        });
    }

    /**
     * The same remote image, re-served from our own origin so the browser may read
     * its bytes.
     *
     * Needed because the resizer answers 200 with the image and no
     * Access-Control-Allow-Origin, which blocks fetch() from reading the body —
     * and no-cors returns an opaque zero-length one, so there is nothing to upload.
     * Going through the backend removes CORS from the question entirely.
     *
     * Only allow-listed hosts are fetched; see AiChat::proxyImage().
     *
     * Through HttpClient rather than fetch(), and that is not incidental: the
     * route is behind jwt.verify, and only HttpClient passes the interceptor that
     * attaches the bearer token. A bare fetch() would arrive unauthenticated.
     */
    fetchImageBlob(url: string, skipLoader = true): Observable<Blob> {
        return this.http.get(this.base + 'proxyImage', {
            params: { url, skipLoader: String(skipLoader) },
            responseType: 'blob'
        });
    }

    /* ---- Download a generated asset (streamed as a file blob) ---- */
    downloadAsset(sessionId: string, assetId: string, assetType: 'image' | 'video' = 'image', skipLoader = true): Observable<Blob> {
        return this.http.get(this.base + 'downloadAsset', {
            params: {
                session_id: sessionId,
                asset_id: assetId,
                asset_type: assetType,
                skipLoader: String(skipLoader)
            },
            responseType: 'blob'
        });
    }
}
